import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Avatar } from "./Avatar";
import { navigationLinks } from "./NavigationLinks";
import { ThemeToggler } from "../common/ThemeToggler";
import { LocaleSwitcher } from "../common/LocaleSwitcher";
import { Icon } from "../common/Icon";
import { useLocale } from "../../context/LocaleContext";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";

interface User {
  username: string;
  avatarUrl?: string;
  role: string;
}

interface MobileMenuProps {
  isOpen: boolean;
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  onNavClick: () => void;
  onLogout: () => void;
  onClose: () => void;
  isAuthPage?: boolean;
  onOpenSearch?: () => void;
  returnTo?: string;
}

/**
 * Full-height overlay sheet.
 *
 * It used to be an inline block that expanded inside the header, which pushed
 * the page down and let the page scroll behind it. Now it covers the screen,
 * scrolls itself, and locks the body — every row is at least 48px.
 */
export function MobileMenu({
  isOpen,
  user,
  isAuthenticated,
  isAdmin,
  onNavClick,
  onLogout,
  onClose,
  isAuthPage = false,
  onOpenSearch,
  returnTo,
}: MobileMenuProps) {
  const shouldShowAuth = isAuthenticated && !isAuthPage;
  const { availableLocales } = useLocale();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useBodyScrollLock(isOpen);

  // Move focus into the sheet so the keyboard and screen readers follow it.
  useEffect(() => {
    if (isOpen) closeRef.current?.focus();
  }, [isOpen]);

  return createPortal(
    <div
      /* overflow-hidden: the closed sheet is translated off-screen and would
         otherwise widen the page's scrollable area on mobile. */
      className={`fixed inset-0 z-[60] overflow-hidden md:hidden ${
        isOpen ? '' : 'pointer-events-none'
      }`}
      aria-hidden={!isOpen}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-[var(--color-overlay)] transition-opacity duration-200 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Sheet */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        className={`absolute inset-y-0 right-0 flex w-full max-w-sm flex-col bg-background shadow-2xl transition-transform duration-200 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Sheet header mirrors the site header so the close button lands where the burger was */}
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-border px-4">
          <span className="font-display text-lg font-extrabold tracking-tight text-foreground">
            Menu
          </span>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="grid h-11 w-11 place-items-center rounded-xl text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Icon name="x" size={22} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4">
          <button
            type="button"
            onClick={onOpenSearch}
            className="flex h-12 w-full items-center gap-3 rounded-xl border border-border bg-surface-container-lowest px-4 text-muted-foreground"
          >
            <Icon name="search" size={18} />
            <span className="text-[15px]">Search jslipi</span>
          </button>

          <ul className="py-2">
            {navigationLinks.map((link) => (
              <li key={link.label}>
                <Link
                  to={link.to}
                  onClick={onNavClick}
                  className="flex h-14 items-center rounded-lg px-2 font-headline text-[22px] font-bold tracking-tight text-foreground transition-colors hover:bg-muted"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2 border-t border-border py-4">
            {availableLocales.length > 1 && (
              <LocaleSwitcher availableLocales={availableLocales} size="md" />
            )}
            <ThemeToggler />
          </div>

          <div className="border-t border-border pt-4">
            {shouldShowAuth ? (
              <>
                <div className="flex h-12 items-center gap-3 px-2">
                  {user && <Avatar username={user.username} src={user.avatarUrl} size="sm" className="shrink-0" />}
                  <span className="text-sm font-semibold text-foreground">{user?.username}</span>
                </div>

                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={onNavClick}
                    className="flex h-12 w-full items-center gap-3 rounded-lg px-2 text-[15px] text-foreground transition-colors hover:bg-muted"
                  >
                    <Icon name="settings" size={18} className="text-muted-foreground" />
                    Admin dashboard
                  </Link>
                )}

                <button
                  onClick={onLogout}
                  className="flex h-12 w-full items-center gap-3 rounded-lg px-2 text-[15px] text-foreground transition-colors hover:bg-muted"
                >
                  <Icon name="logout" size={18} className="text-muted-foreground" />
                  Sign out
                </button>
              </>
            ) : !isAuthPage ? (
              <div className="flex flex-col gap-2.5 pb-2">
                <Link
                  to="/register"
                  state={{ from: returnTo }}
                  onClick={onNavClick}
                  className="flex h-12 items-center justify-center rounded-xl bg-primary text-[15px] font-semibold text-primary-foreground"
                >
                  Join free
                </Link>
                <Link
                  to="/login"
                  state={{ from: returnTo }}
                  onClick={onNavClick}
                  className="flex h-12 items-center justify-center rounded-xl border border-border text-[15px] font-semibold text-foreground"
                >
                  Sign in
                </Link>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
